import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core//Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import moment from "moment";
import { withSnackbar } from "notistack";
import React from "react";
import { connect } from "react-redux";

import LoadingButton from "../../Buttons/Loading";
import * as constants from "../../../constants";
import * as actions from "../../../store/actions";
import * as appsyncOperations from "../../../utils/appsyncOperations";
import { isUserRole } from "../../../utils/user";

const TimeOffRequestDialog = ({
  shift,
  open,
  onClose,
  enqueueSnackbar,
  appUserId,
  userDisplayName,
  approvedByDisplayName,
  onApproved,
  onDenied,
  serviceProviderId,
  onDeletion,
}) => {
  const [approvePending, setApprovePending] = React.useState(false);
  const [rejectPending, setRejectPending] = React.useState(false);
  const [deletePending, setDeletePending] = React.useState(false);

  const approveTimeOff = async () => {
    setApprovePending(true);
    try {
      const approvedShift = await appsyncOperations.approveTimeOff({
        shiftId: shift.shiftId,
        approvedBy: appUserId,
      });
      onApproved(approvedShift);
      enqueueSnackbar("Time off approved.");
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
    setApprovePending(false);
  };
  const rejectTimeOff = async () => {
    setRejectPending(true);
    try {
      const deniedShift = await appsyncOperations.denyTimeOff({
        shiftId: shift.shiftId,
        deniedBy: appUserId,
      });
      onDenied(deniedShift);
      enqueueSnackbar("Time off rejected.");
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
    setRejectPending(false);
  };
  const deleteTimeOff = async () => {
    setDeletePending(true);
    try {
      const deletedShift = await appsyncOperations.deleteShift({
        shiftId: shift.shiftId,
        updatedBy: appUserId,
        serviceProviderId,
      });
      onDeletion(deletedShift);
      enqueueSnackbar("Time off deleted.");
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
    setDeletePending(false);
  };

  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Time-off details</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {shift && (
            <>
              <Grid item xs={12}>
                <TextField
                  value={getTimeOffStatus(shift.status)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  label="Status"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={userDisplayName}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  label="Name"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  value={moment(shift.startTime).format("MMM D, YYYY HH:mm")}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  label="Starts"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  value={moment(shift.endTime).format("MMM D, YYYY HH:mm")}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  label="Ends"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  value={shift.note}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  label="Message"
                  multiline
                  variant="outlined"
                />
              </Grid>
              {shift.status ===
                constants.shift.status.timeOffApproved.value && (
                <Grid item xs={12}>
                  <Typography
                    className={classes.labels}
                    variant="caption"
                    color="textSecondary"
                    gutterBottom
                  >
                    {`Approved by: ${
                      approvedByDisplayName ? approvedByDisplayName : "unknown"
                    } \u2022 ${moment(shift.updatedAt).format("lll")}`}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {isUserRole({ admin: true }) && (
          <>
            {shift !== undefined &&
              shift.status ===
                constants.shift.status.timeOffRequested.value && (
                <>
                  <LoadingButton
                    onClick={approveTimeOff}
                    isLoading={approvePending}
                    color="primary"
                    disabled={approvePending || rejectPending}
                  >
                    Approve
                  </LoadingButton>
                  <LoadingButton
                    onClick={rejectTimeOff}
                    isLoading={rejectPending}
                    color="primary"
                    disabled={rejectPending || approvePending}
                  >
                    Reject
                  </LoadingButton>
                </>
              )}
            {shift !== undefined &&
              shift.status === constants.shift.status.timeOffApproved.value && (
                <LoadingButton
                  onClick={deleteTimeOff}
                  isLoading={deletePending}
                  color="primary"
                >
                  Delete
                </LoadingButton>
              )}
          </>
        )}
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const getTimeOffStatus = (status) => {
  switch (status) {
    case constants.shift.status.timeOffRequested.value:
      return "Pending approval";
    case constants.shift.status.timeOffApproved.value:
      return "Approved";
    default:
      return "?";
  }
};

const useStyles = makeStyles((theme) => ({
  labels: {
    paddingLeft: 14,
    paddingRight: 14,
  },
}));

const mapStateToProps = (state, ownProps) => {
  const appUserId = state.auth.user ? state.auth.user.username : null;
  const shift = ownProps.shift;
  const serviceProviderId =
    state.auth.user.attributes["custom:service_provider_id"];
  if (shift !== undefined) {
    return {
      serviceProviderId,
      appUserId,
      userDisplayName:
        state.users.byId[shift.userId] !== undefined
          ? state.users.byId[shift.userId].displayName
          : undefined,
      approvedByDisplayName: state.users.byId[shift.updatedBy]
        ? state.users.byId[shift.updatedBy].displayName
        : undefined,
    };
  } else {
    return { appUserId, serviceProviderId };
  }
};

export default connect(mapStateToProps)(withSnackbar(TimeOffRequestDialog));
